import { useState, useEffect } from 'react'
import { collection, query, where, orderBy, onSnapshot, Timestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { useAuthState } from './useAuth'
import { useUserData } from './useUserData'
import type { ClientRegistry } from '@/types/models'
import { 
  getClientRegistry, 
  getClientRegistryById,
  createClientRegistry as createClientRegistryFn,
  updateClientRegistry as updateClientRegistryFn,
  upsertClientRegistry as upsertClientRegistryFn,
  incrementTattooCount as incrementTattooCountFn
} from '@/lib/clientRegistryData'

interface UseClientRegistriesProps {
  artistId?: string // Optional artistId to fetch registries for a specific artist
  userId?: string // Optional userId to fetch registries for a specific user (client)
}

/**
 * Hook to manage client registries with real-time updates.
 * Can be used to fetch registries for:
 * - A specific artist (all their clients)
 * - A specific user (all artists they've worked with)
 * - The current authenticated user (if artistId or userId not provided)
 */
export function useClientRegistries({ artistId, userId }: UseClientRegistriesProps = {}) {
  const { user } = useAuthState()
  const { userData } = useUserData()
  const [registries, setRegistries] = useState<ClientRegistry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    // Determine the query parameters
    let queryField: 'artistId' | 'userId' | null = null
    let queryValue: string | null = null

    if (artistId) {
      queryField = 'artistId'
      queryValue = artistId
    } else if (userId) {
      queryField = 'userId'
      queryValue = userId
    } else if (userData?.isArtist && userData?.artistId) {
      // If current user is an artist, fetch their client registries
      queryField = 'artistId'
      queryValue = userData.artistId
    } else if (user?.uid) {
      // Otherwise, fetch registries where current user is the client
      queryField = 'userId'
      queryValue = user.uid
    }

    if (!queryField || !queryValue) {
      setRegistries([])
      setLoading(false)
      return
    }

    setLoading(true)

    // Query registries with real-time listener
    const registriesRef = collection(db, 'clientRegistries')
    const registriesQuery = query(
      registriesRef,
      where(queryField, '==', queryValue),
      orderBy('updatedAt', 'desc')
    )

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      registriesQuery,
      (querySnapshot) => {
        const registriesData: ClientRegistry[] = []

        querySnapshot.forEach((doc) => {
          const data = doc.data()
          registriesData.push({
            id: doc.id,
            artistId: data.artistId,
            userId: data.userId,
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
            tattooCount: data.tattooCount,
            lastTattooDate: data.lastTattooDate instanceof Timestamp 
              ? data.lastTattooDate.toDate() 
              : data.lastTattooDate,
            bookingId: data.bookingId,
            chatId: data.chatId
          })
        })

        setRegistries(registriesData)
        setLoading(false)
        setError(null)
      },
      (err) => {
        console.error('[useClientRegistries] Error listening to client registries:', err)
        setError(err instanceof Error ? err : new Error(String(err)))
        setLoading(false)
      }
    )

    // Cleanup listener on unmount
    return () => unsubscribe()
  }, [user?.uid, userData?.isArtist, userData?.artistId, artistId, userId])

  // Helper functions for CRUD operations
  const createRegistry = async (
    artistIdParam: string,
    userIdParam: string,
    bookingId?: string,
    chatId?: string
  ): Promise<string> => {
    try {
      return await createClientRegistryFn(artistIdParam, userIdParam, bookingId, chatId)
    } catch (err) {
      console.error('[useClientRegistries] Error creating registry:', err)
      throw err
    }
  }

  const updateRegistry = async (
    registryId: string,
    data: Partial<Omit<ClientRegistry, 'id' | 'artistId' | 'userId' | 'createdAt'>>
  ): Promise<void> => {
    try {
      await updateClientRegistryFn(registryId, data)
    } catch (err) {
      console.error('[useClientRegistries] Error updating registry:', err)
      throw err
    }
  }

  const upsertRegistry = async (
    artistIdParam: string,
    userIdParam: string,
    data: {
      bookingId?: string
      chatId?: string
      tattooCount?: number
      lastTattooDate?: Date
    }
  ): Promise<string> => {
    try {
      return await upsertClientRegistryFn(artistIdParam, userIdParam, data)
    } catch (err) {
      console.error('[useClientRegistries] Error upserting registry:', err)
      throw err
    }
  }

  const incrementTattooCount = async (
    artistIdParam: string,
    userIdParam: string,
    tattooDate?: Date
  ): Promise<void> => {
    try {
      await incrementTattooCountFn(artistIdParam, userIdParam, tattooDate)
    } catch (err) {
      console.error('[useClientRegistries] Error incrementing tattoo count:', err)
      throw err
    }
  }

  const getRegistry = async (
    artistIdParam: string,
    userIdParam: string
  ): Promise<ClientRegistry | null> => {
    try {
      return await getClientRegistry(artistIdParam, userIdParam)
    } catch (err) {
      console.error('[useClientRegistries] Error fetching registry:', err)
      throw err
    }
  }

  const getRegistryById = async (registryId: string): Promise<ClientRegistry | null> => {
    try {
      return await getClientRegistryById(registryId)
    } catch (err) {
      console.error('[useClientRegistries] Error fetching registry by ID:', err)
      throw err
    }
  }

  return {
    registries,
    loading,
    error,
    createRegistry,
    updateRegistry,
    upsertRegistry,
    incrementTattooCount,
    getRegistry,
    getRegistryById
  }
}

